import { Form, Formik } from "formik";
import { PlusIcon } from "vega-icons";
import * as Yup from "yup";

import { type RoleNew } from "@vega-api/generated/types/roleNew";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { InputField } from "@vega-core/components/Form/Fields/InputField";
import { FullScreenModal } from "@vega-core/components/Modals/FullScreenModal";
import { FullScreenModalHeader } from "@vega-core/components/Modals/FullScreenModalHeader";
import { Key } from "@vega-core/constants/global";
import { m } from "@vega-core/paraglide/messages";

import { useCollaboratorsV2 } from "../hooks/useCollaboratorsV2";

import { CollaboratorsV2 } from "./CollaboratorsV2";

type Props = {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  roles: RoleNew[];
};

export const InviteCollaboratorsDialogV2 = ({
  isOpen,
  roles,
  setOpen,
}: Props) => {
  const {
    collaboratorsAccordions,
    handleFundVehicleTabChange,
    handleRoleChange,
    deleteCollaboratorsAccordion,
    handleAddAccordion,
    isValid: isCollaboratorsConfigValid,
    handleInviteCollaborators,
    isPending,
    handleClose,
  } = useCollaboratorsV2(setOpen);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required(m.settings_collaborators_validEmail())
      .email(m.settings_collaborators_validEmail()),
  });

  const initialValues = {
    email: "",
  };

  return (
    <FullScreenModal
      open={isOpen}
      setOpen={handleClose}
      skipCloseGuards
      variant="primary"
    >
      <div
        className="flex flex-col h-full w-full"
        data-testid="invite-collaborators-dialog"
      >
        <div className="flex items-center gap-6 h-20 border-b-1 border-border/primary w-full px-2 md:px-6">
          <FullScreenModalHeader
            setIsOpen={handleClose}
            title={m.settings_collaborators_inviteCollaborators()}
          />
        </div>
        <Formik
          initialValues={initialValues}
          onSubmit={handleInviteCollaborators}
          validationSchema={validationSchema}
        >
          {({ isValid: isFormValid, values, submitForm }) => (
            <Form
              className="flex flex-col flex-1"
              onKeyDown={(e) => {
                if (e.key === Key.Enter) {
                  e.preventDefault();
                }
              }}
            >
              <div className="flex-1 overflow-y-auto py-6 px-2 md:px-6">
                <div className="flex flex-col items-center">
                  <div className="w-full max-w-2xl">
                    <InputField
                      label={m.settings_collaborators_inviteCollaboratorEmail()}
                      name="email"
                      placeholder={m.settings_collaborators_inviteCollaboratorEmailPlaceholder()}
                    />
                  </div>
                  <div className="flex flex-col gap-4 w-full max-w-2xl mt-6 mb-4">
                    {collaboratorsAccordions.map((collaboratorsAccordion) => {
                      const disabledRoleIds = collaboratorsAccordions.reduce<
                        string[]
                      >((roleIds, accordion) => {
                        if (
                          accordion.id !== collaboratorsAccordion.id &&
                          accordion.selectedRole.value
                        ) {
                          roleIds.push(accordion.selectedRole.value);
                        }

                        return roleIds;
                      }, []);

                      return (
                        <CollaboratorsV2
                          collaboratorsAccordion={collaboratorsAccordion}
                          deleteCollaboratorsAccordion={
                            deleteCollaboratorsAccordion
                          }
                          disabledRoleIds={disabledRoleIds}
                          handleFundVehicleTabChange={
                            handleFundVehicleTabChange
                          }
                          handleRoleChange={handleRoleChange}
                          key={collaboratorsAccordion.id}
                          roles={roles}
                        />
                      );
                    })}
                  </div>
                  <div className="flex w-full max-w-2xl">
                    <ThemedButton
                      ariaLabel="Add Assignment"
                      className="w-full"
                      dataTestId="add-colloborator-role-button"
                      icon={<PlusIcon className="w-4 h-4" />}
                      onClick={handleAddAccordion}
                      type="button"
                      variant="secondary"
                    >
                      {m.settings_collaborators_inviteCollaboratorAddAssignment()}
                    </ThemedButton>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end h-24 border-t-1 border-border/primary w-full px-13 bg-background/primary">
                <ThemedButton
                  dataTestId="invite-collaborators-button"
                  disabled={
                    !isFormValid ||
                    !isCollaboratorsConfigValid() ||
                    !values.email
                  }
                  isLoading={isPending}
                  onClick={submitForm}
                  type="button"
                >
                  {m.settings_collaborators_inviteCollaborators()}
                </ThemedButton>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </FullScreenModal>
  );
};
